import type { ComponentType } from 'react';

import { requireFields } from 'bundles/naptimejs/util/requireFieldsDecorator';
import type { PaymentProcessorType } from 'bundles/payments-common/common/PaymentProcessorTypes';
import type { ProductType } from 'bundles/payments-common/common/ProductType';
import ProductTypeObject from 'bundles/payments-common/common/ProductType';
import type { SubscriptionPaymentInformation } from 'bundles/payments-common/common/types';
import SubscriptionStatus from 'bundles/subscriptions-common/utils/SubscriptionStatus';

import NaptimeResource from './NaptimeResource';

type Fields = Array<keyof Subscriptions>;

type Query = {
  fields?: Fields;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  subcomponents?: Array<ComponentType<any>>;
};
class Subscriptions extends NaptimeResource {
  static RESOURCE_NAME = 'subscriptions.v1' as const;

  declare id: string;

  declare subscriptionId?: string;

  declare cartId?: number;

  declare isActive: boolean;

  // Timestamp in milliseconds
  declare startsAt?: number;

  declare nextBillingAt: number;

  declare paymentInformation?: SubscriptionPaymentInformation;

  declare productItemId: string;

  declare productSku?: string;

  declare productType: ProductType;

  declare status?: keyof typeof SubscriptionStatus;

  declare paymentProcessorId?: PaymentProcessorType;

  static findByUserAndProduct(userId: $TSFixMe, productId: $TSFixMe, data: $TSFixMe) {
    return this.finder('findByUserAndProduct', Object.assign({ params: { userId, productId } }, data));
  }

  static findByUser(userId: $TSFixMe, data: $TSFixMe) {
    return this.finder('findByUser', Object.assign({ params: { userId } }, data));
  }

  static me({ fields = [], subcomponents }: Query = {}) {
    return this.finder('me', { params: {}, fields, subcomponents });
  }

  @requireFields('status')
  get isCancelled() {
    return this.status === SubscriptionStatus.CANCELLED;
  }

  @requireFields('status')
  get isFreeTrial() {
    return this.status === SubscriptionStatus.FREE_TRIAL;
  }

  @requireFields('status')
  get isInactive() {
    return this.status === SubscriptionStatus.INACTIVE;
  }

  @requireFields('status')
  get isPastDue() {
    return this.status === SubscriptionStatus.PAST_DUE;
  }

  @requireFields('status')
  get hasSubscriptionNextBillDetails() {
    return (
      this.status &&
      [SubscriptionStatus.FREE_TRIAL, SubscriptionStatus.ACTIVE, SubscriptionStatus.PAST_DUE].includes(this.status) &&
      this.nextBillingAt
    );
  }

  @requireFields('productType')
  get isSpecializationSubscription() {
    return this.productType === ProductTypeObject.SPECIALIZATION;
  }

  @requireFields('productType')
  get isCatalogSubscription() {
    return this.productType === ProductTypeObject.CATALOG_SUBSCRIPTION;
  }

  @requireFields('productType')
  get isCredentialTrackSubscription() {
    return (
      this.productType === ProductTypeObject.CREDENTIAL_TRACK_SUBSCRIPTION ||
      this.productType === ProductTypeObject.CREDENTIAL_TRACK_SUBSCRIPTION_V2
    );
  }

  @requireFields('productType')
  get isCourseraPlusSubscription() {
    return this.productType === ProductTypeObject.COURSERA_PLUS_SUBSCRIPTION;
  }

  @requireFields('productType')
  get isCourseraLiteSubscription() {
    return this.productType === ProductTypeObject.COURSERA_TIER_LITE;
  }

  /**
   * Widened to match `paymentWalletId` on the gateway model: also returns the Stripe
   * `paymentMethodId`, as a string. Previously threw for any non-wallet Subscription, and had no
   * call sites, so this is behaviour-preserving.
   */
  @requireFields('paymentInformation')
  get paymentWalletId() {
    const walletId = this.walletPaymentInformation?.paymentWalletId ?? this.stripePaymentInformation?.paymentMethodId;

    return walletId === undefined ? undefined : String(walletId);
  }

  @requireFields('paymentInformation')
  get isAppleInApp() {
    return (
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      this.paymentInformation && !!this.paymentInformation['org.coursera.payment.IapSubscriptionPaymentInformation']
    );
  }

  @requireFields('paymentInformation')
  get stripeSubscriptionPaymentInformation() {
    return (
      this.paymentInformation &&
      'org.coursera.payment.StripeSubscriptionPaymentInformation' in this.paymentInformation &&
      this.paymentInformation['org.coursera.payment.StripeSubscriptionPaymentInformation']
    );
  }

  /*
   * Everything below mirrors the API of subscriptions-common/models/Subscriptions.ts so consumers
   * work on both read paths during the gated migration. Deleted with this file at cutover.
   */

  private get walletPaymentInformation() {
    const info = this.paymentInformation;

    return info && 'org.coursera.payment.WalletSubscriptionPaymentInformation' in info
      ? info['org.coursera.payment.WalletSubscriptionPaymentInformation']
      : undefined;
  }

  private get stripePaymentInformation() {
    const info = this.paymentInformation;

    return info && 'org.coursera.payment.StripeSubscriptionPaymentInformation' in info
      ? info['org.coursera.payment.StripeSubscriptionPaymentInformation']
      : undefined;
  }

  private get razorpayPaymentInformation() {
    const info = this.paymentInformation;

    return info && 'org.coursera.payment.RazorpaySubscriptionPaymentInformation' in info
      ? info['org.coursera.payment.RazorpaySubscriptionPaymentInformation']
      : undefined;
  }

  @requireFields('paymentInformation')
  get isWalletPayment() {
    return !!this.walletPaymentInformation;
  }

  @requireFields('paymentInformation')
  get isStripePayment() {
    return !!this.stripePaymentInformation;
  }

  @requireFields('paymentInformation')
  get isRazorpayPayment() {
    return !!this.razorpayPaymentInformation;
  }

  @requireFields('paymentInformation')
  get isStripeBv() {
    return !!this.stripePaymentInformation?.isStripeBv;
  }

  @requireFields('paymentInformation')
  get isSandboxTransaction() {
    return !!(this.stripePaymentInformation?.isSandBoxTransaction ?? this.razorpayPaymentInformation?.isSandbox);
  }

  @requireFields('paymentInformation')
  get paymentProcessorSubscriptionId() {
    return this.razorpayPaymentInformation?.razorpaySubscriptionId ?? this.stripePaymentInformation?.subscriptionId;
  }
}

export default Subscriptions;
